// Theme resolution.
//
// Three states, not two: "light", "dark", and "follow the system". The third is the
// default and is not the same as either — a user who has never chosen wants the app
// to track their OS at any hour, and collapsing that into a stored "light" freezes
// them there when their machine switches at dusk.
//
// The stored value is therefore the CHOICE, absent when there is none, and the CSS
// falls back to prefers-color-scheme on its own. See the pre-paint script in
// index.html, which reads the same key before the first paint — without it a
// dark-mode user gets a white flash on every load.

export type ThemeMode = "light" | "dark";
export type ThemeChoice = ThemeMode | "system";

/** Shared with the inline pre-paint script in index.html — keep them in step. */
export const THEME_CACHE_KEY = "crown-hr-theme";

function prefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/** The user's explicit choice, or "system" when they have not made one.
 *
 *  Every read is wrapped: localStorage throws outright in some privacy modes, and a
 *  theme preference is never worth failing a page load over. */
export function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_CACHE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

/** What should actually be painted right now. */
export function resolveMode(choice: ThemeChoice = readChoice()): ThemeMode {
  if (choice === "system") return prefersDark() ? "dark" : "light";
  return choice;
}

/** Apply a choice and remember it. "system" REMOVES the stored value rather than
 *  writing a resolved mode, so the app resumes tracking the OS. */
export function applyChoice(choice: ThemeChoice): ThemeMode {
  const root = document.documentElement;
  const mode = resolveMode(choice);
  if (choice === "system") {
    delete root.dataset.theme;
    root.style.colorScheme = "";
  } else {
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
  }
  try {
    if (choice === "system") localStorage.removeItem(THEME_CACHE_KEY);
    else localStorage.setItem(THEME_CACHE_KEY, choice);
  } catch {
    /* storage unavailable — the theme still applies for this session */
  }
  return mode;
}

/** Watch the OS preference, but only while the user is on "system".
 *
 *  Returns an unsubscribe function. The guard matters: without it, an OS switch
 *  would override a deliberate choice, which reads as the app ignoring the toggle. */
export function watchSystemTheme(onChange: (mode: ThemeMode) => void): () => void {
  if (typeof matchMedia !== "function") return () => {};
  const mq = matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    if (readChoice() !== "system") return;
    onChange(applyChoice("system"));
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
